/**
 * Client Sentry ultra-léger pour le frontend (pas de dépendance externe).
 * Fire-and-forget sur le DSN. No-op si VITE_SENTRY_DSN absent.
 *
 * Scrubbing :
 *   - emails dans le message masqués
 *   - pas de cookies / tokens capturés
 */

type SentryEvent = {
  message: string;
  level?: 'error' | 'warning' | 'info';
  exception?: { type: string; value: string; stacktrace?: string };
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  user?: { id?: string; email?: string };
};

function parseDsn(dsn: string) {
  const m = dsn.match(/^(https?):\/\/([^@]+)@([^/]+)\/(\d+)$/);
  if (!m) return null;
  return { protocol: m[1]!, key: m[2]!, host: m[3]!, project: m[4]! };
}

export async function captureFrontException(err: unknown, ctx?: Partial<SentryEvent>) {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  const parsed = parseDsn(dsn);
  if (!parsed) return;

  const e = err instanceof Error ? err : new Error(String(err));
  const event = {
    event_id: (crypto.randomUUID?.() ?? Math.random().toString(36)).replace(/-/g, ''),
    timestamp: new Date().toISOString(),
    platform: 'javascript',
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE,
    message: scrub(e.message),
    level: 'error' as const,
    exception: {
      type: e.name,
      value: scrub(e.message),
      stacktrace: e.stack,
    },
    ...ctx,
    request: {
      url: typeof window !== 'undefined' ? window.location.href : '',
    },
  };

  try {
    await fetch(
      `${parsed.protocol}://${parsed.host}/api/${parsed.project}/store/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Sentry-Auth': `Sentry sentry_version=7, sentry_key=${parsed.key}, sentry_client=tender-copilot-web/1.0`,
        },
        body: JSON.stringify(event),
        // Avoid keeping the tab open waiting on Sentry if slow
        keepalive: true,
      },
    );
  } catch {
    // silent
  }
}

function scrub(s: string): string {
  return s.replace(/[\w.+-]+@[\w.-]+/g, '***@***');
}
